import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    NativeSelect,
    NumberInput,
    Textarea,
    Button,
    Badge,
    Text,
    SegmentedControl,
    Checkbox,
    Flex,
    Accordion,
    Grid,
    Box,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconCalendar, IconMapPin } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { modals } from '@mantine/modals';
import { CurrentPrices, Request, Store } from '../../codegen/graphql';
import { useAppConfig } from '../../hooks/useAppConfig';
import MarginCalculation from './components/MarginCalculation';
import { StoreSelectionItem } from './components/StoreSelectionItem';
import RestrictedStoresNotice from './components/RestrictedStoresNotice';
import {
    BELOW_COST_INTERIM_PRICE_ERROR,
    getExVatPrice,
} from '../../helpers/calculateMargin';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { getRequestEffectiveCostPrice } from '../../helpers/requestZonePricing';
import {
    getHighestCashbuildCostPrice,
    getHighestCostPrice,
    getHighestPnlCostPrice,
} from '../../helpers/priceCalculation';
import { getSuggestedStoreIds } from '../../helpers/priceChangeScope';
import {
    shouldDisablePnlPriceInput,
    shouldDisableCashbuildPriceInput,
    shouldShowCashbuildPriceInput,
    shouldShowPnlPriceInput,
    determineRequiredPriceTypes,
} from '../../helpers/storeHelpers';
import {
    ApplicableTo,
    filterStoreIdsToEligibleSet,
    isStoreSelected,
    parseApplicableTo,
} from '../../helpers/storeSelectionHelper';
import { useHighestCostPriceStore } from '../../hooks/useHighestCostPriceStore';
import { useEligibleAdvertisingZoneStores } from '../../hooks/useEligibleAdvertisingZoneStores';
import { When } from '../common';

type Props = {
    request: Request & { resolvedVatPercentage?: number | null };
    store: Store | null;
    advertisingZoneStores: Store[];
    currentPrices?: CurrentPrices;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: {
            changedPrice?: number;
            changedPriceValidFrom?: string;
            changedPriceValidTo?: string;
            applyToStores?: string[];
            applyImmediately?: boolean;
            pnlPrice?: number;
            cashbuildPrice?: number;
        };
    }) => void;
    close: () => void;
};

const APPLICABLE_TO_OPTIONS = [
    { value: 'zone', label: 'Advertising Zone' },
    { value: 'store', label: 'Store' },
];

const getApplicableToOptions = (lockToZone: boolean) => {
    if (lockToZone) {
        return [APPLICABLE_TO_OPTIONS[0]];
    }
    return APPLICABLE_TO_OPTIONS;
};

function SuggestInterimPriceForm({
    request,
    store,
    advertisingZoneStores,
    currentPrices,
    onConfirm,
    close,
}: Props) {
    const { interimPriceReasons, vatPercentage: fallbackVatPercentage } =
        useAppConfig();
    const resolvedVatPercentage =
        request?.resolvedVatPercentage ?? fallbackVatPercentage;

    const isFromSurvey = Boolean(request?.isFromPriceSurvey);
    const {
        eligibleZoneStores,
        eligibleZoneStoreIds,
        hasEligibleZoneStores,
        defaultApplicableTo,
    } = useEligibleAdvertisingZoneStores(advertisingZoneStores);
    const lockApplicableToZone = isFromSurvey && hasEligibleZoneStores;
    const applicableToDirtyRef = useRef(false);
    const selectedStoresDirtyRef = useRef(false);
    const highestCostPriceStoreId =
        useHighestCostPriceStore(eligibleZoneStores);
    const totalEligible = eligibleZoneStores.length;
    const stores = useMemo(() => {
        if (hasEligibleZoneStores) {
            return eligibleZoneStores;
        }
        if (store) {
            return [store];
        }
        return [];
    }, [hasEligibleZoneStores, eligibleZoneStores, store]);

    const initialSelectedStoreIds = useMemo(() => {
        const suggested = filterStoreIdsToEligibleSet(
            getSuggestedStoreIds(request),
            eligibleZoneStores,
        );
        if (suggested.length > 0) {
            return suggested;
        }
        return eligibleZoneStoreIds;
    }, [request, eligibleZoneStores, eligibleZoneStoreIds]);

    const getSelectedStoresList = useCallback(
        (applicableToValue: ApplicableTo, selectedStores: string[]) => {
            if (applicableToValue === 'zone') {
                return eligibleZoneStores.filter(zoneStore =>
                    selectedStores.includes(zoneStore.id),
                );
            }
            if (store) {
                return [store];
            }
            return [];
        },
        [eligibleZoneStores, store],
    );

    const validatePriceChangeDurationEnd = (
        endDate: Date | null,
        noEndDate: boolean,
    ): string | null => {
        if (noEndDate) {
            return null;
        }
        if (endDate) {
            return null;
        }
        return 'End date is required.';
    };

    const [applicableTo, setApplicableTo] = useState(defaultApplicableTo);
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>(
        initialSelectedStoreIds,
    );

    const effectiveStores = useMemo(() => {
        if (applicableTo === 'store') {
            return store ? [store] : [];
        }
        return stores.filter(s => selectedStoreIds.includes(s.id));
    }, [applicableTo, selectedStoreIds, stores, store]);

    const fallbackCostPrice = getRequestEffectiveCostPrice(request ?? {});
    const selectionRef = useRef({
        getSelectedStoresList,
        fallbackCostPrice,
    });
    useEffect(() => {
        selectionRef.current = { getSelectedStoresList, fallbackCostPrice };
    }, [getSelectedStoresList, fallbackCostPrice]);

    const costForSelection = useCallback(
        (applicableToValue: ApplicableTo, selectedStores: string[]) => {
            const selected = selectionRef.current.getSelectedStoresList(
                applicableToValue,
                selectedStores,
            );
            return (
                getHighestCostPrice(selected) ??
                selectionRef.current.fallbackCostPrice ??
                0
            );
        },
        [],
    );

    const cashbuildCostPrice = useMemo(
        () =>
            getHighestCashbuildCostPrice(effectiveStores) ?? fallbackCostPrice,
        [effectiveStores, fallbackCostPrice],
    );
    const pnlCostPrice = useMemo(
        () => getHighestPnlCostPrice(effectiveStores) ?? fallbackCostPrice,
        [effectiveStores, fallbackCostPrice],
    );

    const validateNewPrice = (
        value: number | undefined,
        needsCashbuildPrice: boolean,
        applicableToValue: ApplicableTo,
        selectedStores: string[],
    ): string | null => {
        if (!needsCashbuildPrice) {
            return null;
        }
        return getExVatPrice(value ?? 0, resolvedVatPercentage) >=
            costForSelection(applicableToValue, selectedStores)
            ? null
            : BELOW_COST_INTERIM_PRICE_ERROR;
    };

    const validatePnlPrice = (
        value: number | undefined | null,
        needsPnlPrice: boolean,
        applicableToValue: ApplicableTo,
        selectedStores: string[],
    ): string | null => {
        if (!needsPnlPrice) {
            return null;
        }

        if (value === undefined || value === null || Number.isNaN(value)) {
            return 'P&L price is required.';
        }

        return getExVatPrice(value, resolvedVatPercentage) >=
            costForSelection(applicableToValue, selectedStores)
            ? null
            : 'P&L price (ex VAT) must be more than the cost price.';
    };

    const form = useForm<{
        applicableTo: ApplicableTo;
        newPrice: number;
        pnlPrice?: number;
        reason: string;
        priceChangeDurationStart: Date | null;
        priceChangeDurationEnd: Date | null;
        noEndDate: boolean;
        immediatePriceChange: boolean;
        selectedStores: string[];
        notes: string;
    }>({
        initialValues: {
            applicableTo: defaultApplicableTo,
            newPrice: request?.proposedPrice,
            pnlPrice: request?.proposedPrice,
            reason: interimPriceReasons[0] ?? '',
            priceChangeDurationStart: request?.proposedValidFrom
                ? new Date(request.proposedValidFrom)
                : dayjs().add(1, 'day').toDate(),
            priceChangeDurationEnd: null,
            noEndDate: false,
            immediatePriceChange: false,
            selectedStores: initialSelectedStoreIds,
            notes: '',
        },
        validate: values => {
            const selectedStoresList = getSelectedStoresList(
                values.applicableTo,
                values.selectedStores,
            );

            const zoneHasStores =
                values.applicableTo !== 'zone' ||
                filterStoreIdsToEligibleSet(
                    values.selectedStores,
                    eligibleZoneStores,
                ).length > 0;

            const { needsCashbuildPrice, needsPnlPrice } =
                determineRequiredPriceTypes(selectedStoresList);

            return {
                applicableTo: zoneHasStores
                    ? null
                    : 'Select at least one store in the zone.',
                newPrice: validateNewPrice(
                    values.newPrice,
                    needsCashbuildPrice,
                    values.applicableTo,
                    values.selectedStores,
                ),
                pnlPrice: validatePnlPrice(
                    values.pnlPrice,
                    needsPnlPrice,
                    values.applicableTo,
                    values.selectedStores,
                ),
                reason: values.reason ? null : 'Reason is required',
                priceChangeDurationStart: values.priceChangeDurationStart
                    ? null
                    : 'Start date is required.',
                priceChangeDurationEnd: validatePriceChangeDurationEnd(
                    values.priceChangeDurationEnd,
                    values.noEndDate,
                ),
            };
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['newPrice'],
    });

    useEffect(() => {
        if (!applicableToDirtyRef.current) {
            if (form.values.applicableTo !== defaultApplicableTo) {
                form.setFieldValue('applicableTo', defaultApplicableTo);
            }
        }

        if (!hasEligibleZoneStores) {
            return;
        }

        if (!selectedStoresDirtyRef.current) {
            form.setFieldValue('selectedStores', initialSelectedStoreIds);
            return;
        }

        const filteredSelectedStores = filterStoreIdsToEligibleSet(
            form.values.selectedStores,
            eligibleZoneStores,
        );
        if (
            filteredSelectedStores.length !== form.values.selectedStores.length
        ) {
            form.setFieldValue('selectedStores', filteredSelectedStores);
        }
    }, [
        defaultApplicableTo,
        eligibleZoneStores,
        hasEligibleZoneStores,
        initialSelectedStoreIds,
    ]);

    useEffect(() => {
        if (interimPriceReasons.length > 0 && !form.values.reason) {
            form.setFieldValue('reason', interimPriceReasons[0]);
        }
    }, [interimPriceReasons]);

    useEffect(() => {
        setApplicableTo(form.values.applicableTo);
        setSelectedStoreIds(form.values.selectedStores);
    }, [form.values.applicableTo, form.values.selectedStores]);

    const priceTypeInfo = useMemo(() => {
        return determineRequiredPriceTypes(effectiveStores);
    }, [effectiveStores]);

    useEffect(() => {
        if (form.values.newPrice !== undefined) {
            form.validateField('newPrice');
        }
        if (
            form.values.pnlPrice !== undefined &&
            form.values.pnlPrice !== null
        ) {
            form.validateField('pnlPrice');
        }
    }, [priceTypeInfo]);

    const onChangeSelectedStore = (storeId: string, selected: boolean) => {
        selectedStoresDirtyRef.current = true;
        const selectedStores = form.values.selectedStores || [];
        if (selected) {
            form.setFieldValue(
                'selectedStores',
                selectedStores.concat(storeId),
            );
        } else {
            form.setFieldValue(
                'selectedStores',
                selectedStores.filter(id => id !== storeId),
            );
        }
    };

    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    const renderDynamicContent = useCallback(() => {
        const applicableToStore = form.values.applicableTo === 'store';
        return (
            <div className="flex flex-col gap-2">
                <When condition={applicableToStore}>
                    <>
                        <Text>Applying to Store</Text>
                        <div className="flex gap-2 items-center">
                            <IconMapPin size={18} />
                            <Text className="text-[#4D5E80] Capitalize">
                                {store?.name || request?.storeName || 'N/A'}
                            </Text>
                            <Badge size="sm" variant="light">
                                {store?.storeNo || request?.storeNo || 'N/A'}
                            </Badge>
                        </div>
                    </>
                </When>
                <RestrictedStoresNotice
                    advertisingZoneStores={advertisingZoneStores}
                />
                <When condition={!applicableToStore}>
                    <Accordion
                        styles={{
                            control: {
                                paddingLeft: 0,
                                paddingRight: 0,
                            },
                            item: {
                                border: 'none',
                            },
                        }}
                    >
                        <Accordion.Item value="collapsible">
                            <Accordion.Control styles={{}}>
                                <Text>
                                    {form.values.selectedStores.length ===
                                    totalEligible
                                        ? `All ${form.values.selectedStores.length}`
                                        : `Selected ${form.values.selectedStores.length}`}{' '}
                                    stores in Advertising Zone
                                </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                {eligibleZoneStores.map(zoneStore => (
                                    <StoreSelectionItem
                                        key={zoneStore.id}
                                        store={zoneStore}
                                        isSelected={isStoreSelected(
                                            zoneStore.id,
                                            form.values.selectedStores,
                                        )}
                                        onToggle={onChangeSelectedStore}
                                        isHighestCostPrice={
                                            zoneStore.id ===
                                            highestCostPriceStoreId
                                        }
                                    />
                                ))}
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </When>
            </div>
        );
    }, [
        form.values.applicableTo,
        form.values.selectedStores,
        store,
        request,
        eligibleZoneStores,
        highestCostPriceStoreId,
        totalEligible,
    ]);

    const handleClose = () => {
        form.reset();
        close();
    };

    const openConfirmZoneApplicationModal = () => {
        modals.openConfirmModal({
            id: 'confirm-zone-application',
            title: 'Apply Change To A Zone?',
            modalId: 'confirm-zone-application',
            children: (
                <div className="p-2">
                    <Text>
                        Are you sure you want to apply this change request to
                        all the selected stores in the advertising zone?
                    </Text>
                </div>
            ),
            onConfirm: () => {
                const selectedStoresList = eligibleZoneStores.filter(
                    zoneStore =>
                        form.values.selectedStores.includes(zoneStore.id),
                );

                const { needsCashbuildPrice, needsPnlPrice } =
                    determineRequiredPriceTypes(selectedStoresList);

                let cashbuildPrice: number | undefined;
                let pnlPrice: number | undefined;

                if (needsCashbuildPrice) {
                    cashbuildPrice = Number(form.values.newPrice);
                }

                if (needsPnlPrice) {
                    pnlPrice = form.values.pnlPrice
                        ? Number(form.values.pnlPrice)
                        : undefined;
                }

                const metadata = {
                    cashbuildPrice,
                    pnlPrice,
                    changedPriceValidFrom: dayjs(
                        form.values.priceChangeDurationStart || new Date(),
                    ).toISOString(),
                    changedPriceValidTo: dayjs(
                        form.values.priceChangeDurationEnd || new Date(),
                    ).toISOString(),
                    reason: form.values.reason,
                    applyImmediately: form.values.immediatePriceChange,
                    applyToStores: filterStoreIdsToEligibleSet(
                        form.values.selectedStores,
                        eligibleZoneStores,
                    ),
                };
                onConfirm({
                    outcome: 'ApprovedWithTemporaryPrice',
                    notes: form.values.notes,
                    metadata,
                });
                handleClose();
            },
            classNames: {
                title: 'text-primary font-semibold text-lg',
                body: 'p-4',
                inner: 'z-[1200]',
                overlay: 'z-[1000]',
            },
            labels: {
                confirm: 'Confirm',
                cancel: 'Cancel',
            },

            confirmProps: {
                fullWidth: false,
                size: 'md',
            },
            cancelProps: {
                variant: 'outline',
                fullWidth: false,
                size: 'md',
            },
        });
    };

    const onChangeNoEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('noEndDate', event.currentTarget.checked);
        form.setFieldValue(
            'priceChangeDurationEnd',
            event.currentTarget.checked ? dayjs('9999-12-31').toDate() : null,
        );
    };

    const handleSubmit = form.onSubmit(values => {
        try {
            if (values.applicableTo === 'zone') {
                openConfirmZoneApplicationModal();
                return;
            }

            const selectedStoresList = store ? [store] : [];

            const { needsCashbuildPrice, needsPnlPrice } =
                determineRequiredPriceTypes(selectedStoresList);

            let cashbuildPrice: number | undefined;
            let pnlPrice: number | undefined;

            if (needsCashbuildPrice) {
                cashbuildPrice = Number(values.newPrice);
            }

            if (needsPnlPrice) {
                pnlPrice = values.pnlPrice
                    ? Number(values.pnlPrice)
                    : undefined;
            }

            onConfirm({
                outcome: 'ApprovedWithTemporaryPrice',
                notes: values.notes,
                metadata: {
                    cashbuildPrice,
                    pnlPrice,
                    changedPriceValidFrom: dayjs(
                        values.priceChangeDurationStart || new Date(),
                    ).toISOString(),
                    changedPriceValidTo: dayjs(
                        values.priceChangeDurationEnd || new Date(),
                    ).toISOString(),
                    applyImmediately: values.immediatePriceChange,
                },
            });

            handleClose();
        } catch (error: any) {
            form.setFieldError('notes', error.message);
        }
    });

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
                <When condition={hasEligibleZoneStores}>
                    <>
                        <Text c="#212529">Adjustment Applicable To</Text>
                        <SegmentedControl
                            data={getApplicableToOptions(lockApplicableToZone)}
                            fullWidth
                            color="primary"
                            classNames={{
                                root: 'mt-1',
                            }}
                            disabled={lockApplicableToZone}
                            {...form.getInputProps('applicableTo')}
                            onChange={value => {
                                applicableToDirtyRef.current = true;
                                const nextApplicableTo =
                                    parseApplicableTo(value);
                                if (!nextApplicableTo) {
                                    return;
                                }
                                form.setFieldValue(
                                    'applicableTo',
                                    nextApplicableTo,
                                );
                            }}
                        />
                    </>
                </When>
                {!!form.errors?.applicableTo && (
                    <Text color="#212529" className="mt-2 text-red-500 text-sm">
                        {form.errors.applicableTo}
                    </Text>
                )}
            </div>
            {renderDynamicContent()}
            <NativeSelect
                data={interimPriceReasons}
                label="Reason for Interim price change"
                placeholder="Select a reason"
                required
                {...form.getInputProps('reason')}
            />
            <Grid>
                {(() => {
                    const isStoreLevel = form.values.applicableTo === 'store';
                    const isCashbuildDisabled = isStoreLevel
                        ? shouldDisableCashbuildPriceInput({ store })
                        : !form.values.selectedStores.some(id => {
                              const selectedZoneStore = eligibleZoneStores.find(
                                  zoneStore => zoneStore.id === id,
                              );
                              return (
                                  !selectedZoneStore?.storeNo?.startsWith(
                                      'P',
                                  ) || selectedZoneStore?.isTradingAsCashbuild
                              );
                          });

                    const isPnlDisabled = isStoreLevel
                        ? !(store?.storeNo?.startsWith('P') ?? false) ||
                          (store?.isTradingAsCashbuild ?? false)
                        : shouldDisablePnlPriceInput(
                              eligibleZoneStores.filter(zoneStore =>
                                  form.values.selectedStores.includes(
                                      zoneStore.id,
                                  ),
                              ),
                          );

                    const shouldShowCashbuild = isStoreLevel
                        ? !isCashbuildDisabled
                        : shouldShowCashbuildPriceInput(eligibleZoneStores);

                    const shouldShowPnl = isStoreLevel
                        ? !isPnlDisabled
                        : shouldShowPnlPriceInput(eligibleZoneStores);

                    const showSingleInput =
                        (isStoreLevel &&
                            (isCashbuildDisabled || isPnlDisabled)) ||
                        (!isStoreLevel &&
                            (!shouldShowCashbuild || !shouldShowPnl));

                    return (
                        <>
                            {shouldShowCashbuild && (
                                <Grid.Col span={showSingleInput ? 12 : 6}>
                                    <NumberInput
                                        label="New Cashbuild Price"
                                        placeholder="Enter Cashbuild price"
                                        required
                                        min={0}
                                        step={0.1}
                                        decimalScale={2}
                                        disabled={
                                            !isStoreLevel && isCashbuildDisabled
                                        }
                                        rightSection={
                                            <Text className="pr-8 pl-2">
                                                {productCurrency}
                                            </Text>
                                        }
                                        {...form.getInputProps('newPrice')}
                                    />
                                    <Box mt="md">
                                        <MarginCalculation
                                            costPrice={cashbuildCostPrice}
                                            currentSellingPrice={
                                                request?.currentPrice ?? 0
                                            }
                                            proposedPrice={form.values.newPrice}
                                            benchmarkMargin={
                                                request?.benchmarkMargin ?? 0
                                            }
                                            currency={productCurrency}
                                            completed={request?.completed}
                                            previousCostPrice={
                                                request?.previousCostPrice
                                            }
                                            previousSellingPrice={
                                                request?.previousSellingPrice
                                            }
                                            previousCashbuildPrice={
                                                request?.previousCashbuildPrice
                                            }
                                            previousPnlPrice={
                                                request?.previousPnlPrice
                                            }
                                            priceType="cashbuild"
                                            currentPrices={currentPrices}
                                            layout={
                                                showSingleInput
                                                    ? 'row'
                                                    : 'column'
                                            }
                                            vatPercentage={
                                                resolvedVatPercentage
                                            }
                                        />
                                    </Box>
                                </Grid.Col>
                            )}

                            {shouldShowPnl && (
                                <Grid.Col span={showSingleInput ? 12 : 6}>
                                    <NumberInput
                                        label="New P&L Price"
                                        placeholder="Enter P&L price"
                                        min={0}
                                        step={0.1}
                                        decimalScale={2}
                                        disabled={
                                            !isStoreLevel && isPnlDisabled
                                        }
                                        rightSection={
                                            <Text className="pr-8 pl-2">
                                                {productCurrency}
                                            </Text>
                                        }
                                        {...form.getInputProps('pnlPrice')}
                                    />
                                    <Box mt="md">
                                        <MarginCalculation
                                            costPrice={pnlCostPrice}
                                            currentSellingPrice={
                                                request?.currentPrice ?? 0
                                            }
                                            proposedPrice={
                                                form.values.pnlPrice || 0
                                            }
                                            benchmarkMargin={
                                                request?.benchmarkMargin ?? 0
                                            }
                                            currency={productCurrency}
                                            completed={request?.completed}
                                            previousCostPrice={
                                                request?.previousCostPrice
                                            }
                                            previousSellingPrice={
                                                request?.previousSellingPrice
                                            }
                                            previousCashbuildPrice={
                                                request?.previousCashbuildPrice
                                            }
                                            previousPnlPrice={
                                                request?.previousPnlPrice
                                            }
                                            priceType="pnl"
                                            currentPrices={currentPrices}
                                            layout={
                                                showSingleInput
                                                    ? 'row'
                                                    : 'column'
                                            }
                                            vatPercentage={
                                                resolvedVatPercentage
                                            }
                                        />
                                    </Box>
                                </Grid.Col>
                            )}
                        </>
                    );
                })()}
            </Grid>

            <Flex gap={18}>
                <DatePickerInput
                    required
                    size="md"
                    radius="lg"
                    minDate={new Date()}
                    label="Price Start Date"
                    popoverProps={{
                        withinPortal: true,
                        zIndex: 999,
                        position: 'top',
                    }}
                    placeholder="Pick start date"
                    className="flex-1"
                    leftSection={<IconCalendar size={18} />}
                    {...form.getInputProps('priceChangeDurationStart')}
                />
                <Flex direction="column" gap={12} className="flex-1">
                    <DatePickerInput
                        required
                        size="md"
                        radius="lg"
                        minDate={
                            dayjs(form.values.priceChangeDurationStart)
                                .add(1, 'day')
                                .toDate() || new Date()
                        }
                        label="Price End Date"
                        popoverProps={{
                            withinPortal: true,
                            zIndex: 999,
                            position: 'top',
                        }}
                        disabled={form.values.noEndDate}
                        placeholder="Pick end date"
                        leftSection={<IconCalendar size={18} />}
                        {...form.getInputProps('priceChangeDurationEnd')}
                    />
                    <Checkbox
                        label="No end date"
                        {...form.getInputProps('noEndDate')}
                        onChange={onChangeNoEndDate}
                    />
                </Flex>
            </Flex>

            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />

            {dayjs(form.values.priceChangeDurationStart).isSame(
                dayjs(),
                'day',
            ) && (
                <Checkbox
                    label="Apply price change immediately"
                    {...form.getInputProps('immediatePriceChange')}
                />
            )}

            <div className="flex gap-4">
                <Button size="md" variant="outline" onClick={handleClose}>
                    Cancel
                </Button>
                <Button size="md" type="submit">
                    Proceed
                </Button>
            </div>
        </form>
    );
}

export default SuggestInterimPriceForm;
